import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check } from "lucide-react";
import { Field, Input, PasswordInput, PasswordStrength, Checkbox } from "@/portal/ui/Field";
import { Button } from "@/portal/ui/Button";
import "@/portal/theme.css";

const STEPS = ["Date de contact", "Parolă nouă", "Termeni"];

/** Prima autentificare a clientului: confirmă datele → parolă nouă → acceptă termenii. Nu poate fi ocolit. */
export default function OnboardingGate() {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "", cur: "", np: "", np2: "", terms: false });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const userName = user && typeof user === "object" ? user.name : "";
  const userPhone = user && typeof user === "object" ? user.phone : "";
  useEffect(() => {
    setF((x) => ({ ...x, name: x.name || userName || "", phone: x.phone || userPhone || "" }));
  }, [userName, userPhone]);

  if (!user || typeof user !== "object") return null;
  const needs = user.role === "client" && (user.must_change_password || user.terms_accepted === false);
  if (!needs) return null;

  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setErrors({}); };

  function next() {
    const errs = {};
    if (step === 0 && !f.name.trim()) errs.name = "Introdu numele tău complet.";
    if (step === 1) {
      if (!f.cur) errs.cur = "Introdu parola temporară primită de la echipa Brisk.";
      if (f.np.length < 8) errs.np = "Parola nouă trebuie să aibă cel puțin 8 caractere.";
      if (f.np && f.np2 !== f.np) errs.np2 = "Parolele nu coincid.";
      if (f.np && f.cur && f.np === f.cur) errs.np = "Alege o parolă diferită de cea temporară.";
    }
    setErrors(errs); if (Object.keys(errs).length) return;
    setStep((s) => s + 1);
  }

  async function submit() {
    if (!f.terms) { setErrors({ terms: "Pentru a continua, trebuie să accepți Termenii și Condițiile." }); return; }
    setBusy(true);
    try {
      if (f.name.trim() !== user.name || (f.phone || "") !== (user.phone || "")) await api.put("/auth/profile", { name: f.name.trim(), phone: f.phone });
      const { data } = await api.post("/auth/complete-onboarding", { current_password: f.cur, new_password: f.np, accept_terms: true });
      setUser({ ...data, name: f.name.trim(), phone: f.phone });
      toast.success("Contul este configurat. Bine ai venit!");
    } catch (e) {
      const msg = apiErr(e);
      if (msg.toLowerCase().includes("parola actual")) { setStep(1); setErrors({ cur: msg }); } else toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-[#14161A]/60 backdrop-blur-[2px]" />
        <DialogPrimitive.Content data-testid="onboarding-gate" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}
          className="cp fixed z-[201] inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-[520px] bg-cp-surface rounded-t-[14px] sm:rounded-[var(--cp-radius-lg)] shadow-[var(--cp-shadow-lg)] max-h-[94vh] overflow-y-auto focus:outline-none">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-10 w-10 rounded-[var(--cp-radius)] bg-cp-accent-soft text-cp-accent grid place-items-center"><ShieldCheck className="h-5 w-5" aria-hidden="true" /></span>
              <div>
                <DialogPrimitive.Title className="cp-h2 !text-[22px]">Bun venit în Portalul Brisk</DialogPrimitive.Title>
                <DialogPrimitive.Description className="cp-body-sm">Trei pași scurți pentru a-ți securiza contul.</DialogPrimitive.Description>
              </div>
            </div>

            <ol className="flex items-center gap-2 mb-7" aria-label="Pași">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-2 flex-1 min-w-0" aria-current={i === step ? "step" : undefined}>
                  <span className={`h-6 w-6 rounded-full grid place-items-center text-[11.5px] font-semibold shrink-0 ${i < step ? "bg-cp-success text-white" : i === step ? "bg-cp-accent text-white" : "bg-cp-neutral-soft text-cp-text-3"}`}>{i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
                  <span className={`text-[12.5px] truncate ${i === step ? "text-cp-text font-medium" : "text-cp-text-3"}`}>{s}</span>
                  {i < STEPS.length - 1 && <span className="flex-1 h-px bg-cp-border" aria-hidden="true" />}
                </li>
              ))}
            </ol>

            {step === 0 && (
              <div className="space-y-4 cp-rise" data-testid="ob-step-contact">
                <p className="cp-body">Confirmă datele cu care te va contacta echipa de proiect.</p>
                <Field label="Nume complet" required error={errors.name}>{(p) => <Input {...p} value={f.name} onChange={(e) => set({ name: e.target.value })} autoComplete="name" data-testid="ob-name" />}</Field>
                <Field label="Telefon" hint="Opțional, dar util pentru situații urgente.">{(p) => <Input {...p} type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+40 7xx xxx xxx" autoComplete="tel" data-testid="ob-phone" />}</Field>
                <Field label="Email de autentificare">{(p) => <Input {...p} value={user.email} readOnly />}</Field>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4 cp-rise" data-testid="ob-step-password">
                <p className="cp-body">Înlocuiește parola temporară cu una personală, cunoscută doar de tine.</p>
                <Field label="Parola temporară (primită de la echipă)" required error={errors.cur}>{(p) => <PasswordInput {...p} value={f.cur} onChange={(e) => set({ cur: e.target.value })} autoComplete="current-password" data-testid="ob-current" />}</Field>
                <Field label="Parola nouă" required error={errors.np}>{(p) => <><PasswordInput {...p} value={f.np} onChange={(e) => set({ np: e.target.value })} autoComplete="new-password" data-testid="ob-new" /><PasswordStrength value={f.np} /></>}</Field>
                <Field label="Confirmă parola nouă" required error={errors.np2}>{(p) => <PasswordInput {...p} value={f.np2} onChange={(e) => set({ np2: e.target.value })} autoComplete="new-password" data-testid="ob-confirm" />}</Field>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4 cp-rise" data-testid="ob-step-terms">
                <div className="rounded-[var(--cp-radius)] border border-cp-border bg-cp-bg-subtle p-4 text-[13.5px] text-cp-text-2 leading-relaxed max-h-44 overflow-y-auto">
                  <p className="font-semibold text-cp-text mb-1">Pe scurt</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Informațiile din portal sunt confidențiale și destinate exclusiv beneficiarului proiectului.</li>
                    <li>Deciziile înregistrate în portal (aprobări, respingeri, confirmări) au valoare de comunicare oficială și sunt arhivate cu autor, dată și oră.</li>
                    <li>Contul este personal; nu partaja credențialele cu terți.</li>
                  </ul>
                  <p className="mt-2">Textul integral: <a href="/termeni" target="_blank" rel="noreferrer" className="text-cp-accent hover:underline">Termeni și Condiții</a>.</p>
                </div>
                <Checkbox checked={f.terms} onChange={(e) => set({ terms: e.target.checked })} data-testid="ob-terms"
                  label={<>Am citit și accept <a href="/termeni" target="_blank" rel="noreferrer" className="text-cp-accent hover:underline">Termenii și Condițiile</a> de utilizare a portalului.</>} />
                {errors.terms && <p role="alert" className="text-[12.5px] text-cp-danger">{errors.terms}</p>}
              </div>
            )}

            <div className="mt-7 flex items-center justify-between gap-3">
              {step > 0 ? <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={busy} data-testid="ob-back">Înapoi</Button> : <span />}
              {step < 2 ? <Button onClick={next} data-testid="ob-next">Continuă</Button>
                : <Button onClick={submit} loading={busy} data-testid="ob-submit">Finalizează configurarea</Button>}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
